import {
    Component,
    DoCheck,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { GraphsService } from '@service/graphs/graphs.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType, getAnalogTypesSet } from 'icreate/common/models/channel-type';
import {
    ExhaustChannel,
    ExhaustGraph,
    Graph,
    MAX_CYLINDER_CHANNELS,
    MAX_TURBO_CHANNELS,
    Turbocharger,
} from 'icreate/common/models/graphs';
import * as _ from 'lodash';
import { lastValueFrom, Observable, Subscription, take } from 'rxjs';

@Component({
    selector: 'app-exhaust-graph-table',
    templateUrl: './exhaust-graph-table.component.html',
    styleUrl: './exhaust-graph-table.component.css',
    standalone: false,
})
export class ExhaustGraphTableComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
    @Input() graph: Graph;
    @Input() closeEvent: Observable<void>;
    @Input() saveEvent: Observable<void>;
    @Output() onUpdateSave: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onCloseGraph: EventEmitter<void> = new EventEmitter<void>();
    @ViewChild('cancelConfirmation') cancelConfirmation: ConfirmationWindowComponent;

    readonly TURBO_TAB = 'turbo';
    readonly CYLINDER_TAB = 'cylinder';

    exhaustGraph: ExhaustGraph;
    initialGraph: ExhaustGraph;
    allowedTypesSet: Set<ChannelType> = getAnalogTypesSet();

    saveIsEnabled: boolean = false;

    closeSubscription: Subscription;
    saveSubscription: Subscription;
    turboChannelUpdated: boolean = false;

    maxTurboSlots = MAX_TURBO_CHANNELS;
    maxCylinderSlots = MAX_CYLINDER_CHANNELS;
    selectedTable: string = this.CYLINDER_TAB;
    selectedRowIndex: number;

    constructor(
        private graphsService: GraphsService,
        private dialog: MatDialog
    ) {}

    async ngOnInit() {
        await this.loadGraph();
        this.closeSubscription = this.closeEvent.subscribe(async () => await this.cancel());
        this.saveSubscription = this.saveEvent.subscribe(async () => await this.save());
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (this.graph && changes.graph && !changes.graph.firstChange) {
            await this.loadGraph();
        }
    }

    ngDoCheck(): void {
        const isEqual = _.isEqual(this.initialGraph, this.exhaustGraph);
        if (isEqual && this.saveIsEnabled) {
            this.onUpdateSave.emit(false);
        } else if (!isEqual && !this.saveIsEnabled) {
            this.onUpdateSave.emit(true);
        } else if (isEqual && !this.saveIsEnabled) {
            this.onUpdateSave.emit(false);
        }
    }

    ngOnDestroy(): void {
        this.closeSubscription?.unsubscribe();
        this.saveSubscription?.unsubscribe();
    }

    async loadGraph() {
        this.graphsService.showLoadingSpinner(true);
        this.exhaustGraph = await this.graphsService.getGraphById(this.graph.id, this.graph.type);
        if (!this.exhaustGraph) {
            this.exhaustGraph = new ExhaustGraph();
            this.exhaustGraph.graphId = this.graph.id;
            this.exhaustGraph.CylinderChannels = [];
            this.exhaustGraph.TurboChannels = [];
        } else {
            if ((this.exhaustGraph as any).averageChannelData) {
                this.exhaustGraph.averageChannel = (this.exhaustGraph as any).averageChannelData;
            } else {
                this.exhaustGraph.averageChannel = null;
            }
            this.exhaustGraph.CylinderChannels.sort((a: any, b: any) => a.rowNumber - b.rowNumber);
            this.exhaustGraph.CylinderChannels = this.exhaustGraph.CylinderChannels.map((channel: any) => {
                return {
                    deviationChannel: channel.deviationChannel,
                    tempChannel: channel.tempChannel,
                };
            });
            this.exhaustGraph.TurboChannels.sort((a: any, b: any) => a.rowNumber - b.rowNumber);
            this.exhaustGraph.TurboChannels = this.exhaustGraph.TurboChannels.map((turbo: any) => {
                return {
                    tempChannel: turbo.tempChannel,
                };
            });
            if (this.exhaustGraph.TurboChannels.length == 0) this.exhaustGraph.TurboChannels = [];
        }
        this.initialGraph = _.cloneDeep(this.exhaustGraph);
        this.graphsService.showLoadingSpinner(false);
    }

    async openChannelSelector() {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.graph.vesselId,
                assignedChannel: this.exhaustGraph.averageChannel,
                allowedTypesSet: new Set([ChannelType.EXH_GAS_MEAN]),
                setShouldDisableTypes: true,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
        if (channelSelected) {
            this.exhaustGraph.averageChannel = channelSelected;
        }
    }

    showAddCylinder() {
        return this.exhaustGraph.CylinderChannels.length < this.maxCylinderSlots;
    }

    addCylinderRow() {
        const newCylinderChannel: ExhaustChannel = {
            tempChannel: undefined,
            deviationChannel: undefined,
        };
        this.exhaustGraph.CylinderChannels.push(newCylinderChannel);
    }

    deleteCylinderRow(index) {
        this.exhaustGraph.CylinderChannels.splice(index, 1);
    }

    showAddTurbo() {
        return this.exhaustGraph.TurboChannels.length < this.maxTurboSlots;
    }

    async addTurboRow() {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.graph.vesselId,
                assignedChannel: null,
                allowedTypesSet: getAnalogTypesSet(),
                setShouldDisableTypes: true,
            },
        });

        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());

        if (channelSelected) {
            const newTurboChannel: Turbocharger = { tempChannel: channelSelected };
            this.exhaustGraph.TurboChannels.push(newTurboChannel);
        }
    }

    onTurboChannelUpdated() {
        this.turboChannelUpdated = true;
    }

    deleteTurboRow(index) {
        this.exhaustGraph.TurboChannels.splice(index, 1);
    }

    async cancel() {
        if (!_.isEqual(this.initialGraph, this.exhaustGraph)) {
            this.cancelConfirmation.setVisible(true);
            let confirmationOption = await lastValueFrom(this.cancelConfirmation.onOptionSelected.pipe(take(1)));
            if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                this.save();
            } else if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                return;
            }
            this.onCloseGraph.emit();
        } else {
            this.onCloseGraph.emit();
        }
    }

    async save() {
        if (_.isEqual(this.initialGraph, this.exhaustGraph)) return;
        try {
            this.graphsService.showLoadingSpinner(true);
            await this.graphsService.updateExhaustGraph(this.exhaustGraph);
            this.initialGraph = _.cloneDeep(this.exhaustGraph);
            this.onUpdateSave.emit(false);
            this.graphsService.showLoadingSpinner(false);
        } catch (err) {
            console.error(err);
            this.graphsService.showLoadingSpinner(false);
        }
    }

    tabSelected(tab: any) {
        this.selectedTable = tab;
    }

    optionsToggled(index: number): void {
        if (this.selectedRowIndex == index) {
            this.selectedRowIndex = null;
        } else {
            this.selectedRowIndex = index;
        }
    }

    get isCylinderTabSelected() {
        return this.selectedTable === this.CYLINDER_TAB;
    }

    get isTurboTabSelected() {
        return this.selectedTable === this.TURBO_TAB;
    }
}
